import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { SelectOption } from '@/components/ui/select'
import { useStages } from '@/features/accounts/api'
import { useBoards, type Board } from '../boards/api'
import { useCatalog } from '../catalogApi'
import { type MatchType, type RuleEvent, type TaskRule, useRuleGroups, useTaskRules } from './api'

/** Görüşme türleri (crm toplantı kaydı; tür verilmeyen görüşme "toplanti" sayılır). */
const MEETING_KINDS = ['demo', 'ziyaret', 'telefon', 'toplanti', 'diger'] as const

/** Başlıktan görev türü kodu (motor bir olayda aynı türü bir kez açar). */
export const slugify = (s: string) =>
  s
    .toLocaleLowerCase('tr')
    .replace(/[ıİ]/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/\{[a-z_]+\}/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 60) || 'gorev'

export type RuleLookups = {
  boards: Board[]
  groups: string[]
  roles: string[]
  /** Koşul değeri seçenekleri; listede olmayan mevcut değer de gösterilir. */
  matchOptions: (event: RuleEvent, matchType: MatchType, current: string) => SelectOption[]
  /** Kuralın okunur koşulu ("Aşama: Teklif" gibi); koşul yoksa boş. */
  conditionLabel: (rule: TaskRule) => string
  /** Kuralın panosu ve kolonu ("Teknik › Bekleyen"). */
  placeLabel: (rule: TaskRule) => string
}

/** Kural ekranında teknik kodları (pano, kolon, aşama, modül…) adlarına çeviren veriler. */
export function useRuleLookups(): RuleLookups {
  const { t } = useTranslation()
  const boards = useBoards()
  const stages = useStages(undefined)
  const catalog = useCatalog()
  const groups = useRuleGroups()
  const rules = useTaskRules()

  return useMemo(() => {
    const boardList = boards.data ?? []
    const stageList = (event: RuleEvent) => (event === 'install.stage_changed' ? stages.data?.install?.stages : stages.data?.sales?.stages) ?? []
    const modules = catalog.data ?? []
    const deliverables = [...new Set((rules.data ?? []).filter((r) => r.matchType === 'deliverable').map((r) => r.matchValue))].sort()

    const options = (event: RuleEvent, matchType: MatchType): SelectOption[] => {
      switch (matchType) {
        case 'stage':
          return stageList(event).map((s) => ({ value: s.code, label: s.name }))
        case 'module':
          return modules.map((m) => ({ value: m.code, label: m.name, keywords: [m.code] }))
        case 'meeting_kind':
          return MEETING_KINDS.map((k) => ({ value: k, label: t(`meetings.kinds.${k}`) }))
        case 'deliverable':
          return deliverables.map((d) => ({ value: d, label: d }))
        default:
          return []
      }
    }
    const nameOf = (event: RuleEvent, matchType: MatchType, value: string) => options(event, matchType).find((o) => o.value === value)?.label ?? value

    return {
      boards: boardList,
      groups: groups.data ?? [],
      roles: [...new Set((rules.data ?? []).map((r) => r.assigneeRole).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'tr')),
      matchOptions: (event, matchType, current) => {
        const list = options(event, matchType)
        return current && !list.some((o) => o.value === current) ? [{ value: current, label: current }, ...list] : list
      },
      conditionLabel: (rule) => {
        if (rule.matchType === 'always' || rule.matchType === 'group') return ''
        if (rule.matchType === 'each_module') return t('projects.rules.match.each_module')
        return `${t(`projects.rules.matchValue.${rule.matchType}`)}: ${nameOf(rule.event, rule.matchType, rule.matchValue)}`
      },
      placeLabel: (rule) => {
        const board = boardList.find((b) => b.code === rule.boardCode)
        const column = board?.columns.find((c) => c.key === rule.columnKey)
        return `${board?.name ?? rule.boardCode} › ${column?.name ?? rule.columnKey}`
      },
    }
  }, [boards.data, stages.data, catalog.data, groups.data, rules.data, t])
}
